import React, { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react';
import { Autocomplete, Avatar, Box, CircularProgress, Container, Paper, Stack, TextField, Typography } from '@mui/material';
import { Vacation } from '../../types/Vacation';
import { Employee } from '../../types/Employee';
import { collection, doc, getDoc, getDocs, getFirestore, limit, orderBy, query, where } from 'firebase/firestore';
import employeeConverter from '../../converters/employeeConverter';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { useDebouncedCallback } from 'use-debounce';

type Props = {
  employee?: Employee;
  vacation?: Vacation;
  onChanged?: (e: Employee, startDate: string, endDate: string) => void;
};
export const VacationRequestForm = (props: Props) => {
  const { vacation, onChanged } = props;
  const eProp = props.employee;
  const editMode = vacation !== undefined;

  const db = getFirestore();

  const [employee, setEmployee] = useState<Employee | undefined>(eProp);
  const [startDate, setStartDate] = useState(DateTime.now().minus({days: 1}).toFormat('yyyyMMdd'));
  const [endDate, setEndDate] = useState(DateTime.now().plus({days: 1}).toFormat('yyyyMMdd'));

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [openAutocomplete, setOpenAutocomplete] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const searchDebounced = useDebouncedCallback((value: string) => {
    setLoadingEmployees(true);
    setEmployees([]);
    getDocs(
      query(collection(db, 'Employees'), 
      where('idNumber', '>=', value), 
      where('idNumber', '<=', value + '\uf8ff'),
      where('status', '==', 'Active'),
      limit(15),
      orderBy('idNumber'))
    .withConverter(employeeConverter)).then((qSnapshot) => {
      setEmployees(qSnapshot.docs.map((d) => d.data()));
      setLoadingEmployees(false);
    });
  }, 1000);

  const handleOpenAutocomplete = () => setOpenAutocomplete(true);
  const handleCloseAutocomplete = () => setOpenAutocomplete(false);

  const handleTexInputOnChanged = (event: ChangeEvent<HTMLInputElement>) => {
    searchDebounced(event.currentTarget.value);
  };

  const handleAutocompleteSelectChange = (_e: SyntheticEvent<Element, Event>, value: Employee | null) => {
    setEmployee(value ?? undefined);
    if (value) {
      onChanged && onChanged(value, startDate, endDate);
    }
  };

  useEffect(() => {
    if (editMode) {
      // Get employee information
      const { idNumber, dateStart, dateEnd } = vacation;
      setStartDate(DateTime.fromFormat(dateStart, 'yyyyMMdd').toFormat('MMMM dd, yyyy'));
      setEndDate(DateTime.fromFormat(dateEnd, 'yyyyMMdd').toFormat('MMMM dd, yyyy'));
      if (eProp === undefined) {
        getDoc(doc(db, `Employees/${idNumber}`).withConverter(employeeConverter)).then(docSnapshot => {
          setEmployee(docSnapshot.data());
        });
      }
    } else {
      if (eProp) {
        setEmployee(employee);
        setStartDate(DateTime.now().toFormat('MMMM dd, yyyy'));
        setEndDate(DateTime.now().toFormat('MMMM dd, yyyy'));
      }
    }
  }, [editMode]);

  return (
    <Stack spacing={2.5}>
      <Box>
        {employee !== undefined ? (
          <Box mb={2}>
            <Stack direction='row' spacing={2}>
              <Avatar src={employee.picture} />
              <Stack>
                <Typography fontWeight='700'>{employee.name}</Typography>
                <Typography variant='caption'>{employee.idNumber}</Typography>
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Autocomplete
              id="asynchronous-demo"
              fullWidth
              open={openAutocomplete}
              onOpen={handleOpenAutocomplete}
              onClose={handleCloseAutocomplete}
              isOptionEqualToValue={(option, value) => option.idNumber === value.idNumber}
              getOptionLabel={option => `${option.idNumber} - ${option.name}`}
              options={employees}
              loading={loadingEmployees}
              noOptionsText="Enter employee id number..."
              onChange={handleAutocompleteSelectChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Id Number"
                  onChange={handleTexInputOnChanged}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingEmployees ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Box>
        )}
      </Box>
      {employee && (
        <>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DatePicker
              label="Vacation Start Date"
              format="MMMM dd, yyyy"
              value={DateTime.fromFormat(startDate, 'yyyyMMdd')}
              onChange={(selectedValue: DateTime | null) => {
                if (selectedValue !== null) {
                  const dateString = selectedValue.toFormat('yyyyMMdd');
                  setStartDate(dateString);
                  if (selectedValue > DateTime.fromFormat(endDate, 'yyyyMMdd')) {
                    setEndDate(dateString);
                    onChanged && onChanged(employee, dateString, dateString);
                  } else {
                    onChanged && onChanged(employee, dateString, endDate);
                  }
                }
              }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DatePicker
              label="Vacation End Date"
              format="MMMM dd, yyyy"
              value={DateTime.fromFormat(endDate, 'yyyyMMdd')}
              shouldDisableDate={d => {
                if (typeof(d) === 'object') {
                  const dd = d as DateTime;
                  const start = DateTime.fromFormat(startDate, 'yyyyMMdd');
                  if (dd < start) {
                    return true;
                  }
                }
                return false;
              }}
              onChange={(selectedDate: DateTime | null) => {
                if (selectedDate !== null) {
                  const dateString = selectedDate.toFormat('yyyyMMdd');
                  setEndDate(dateString);
                  onChanged && onChanged(employee, startDate, dateString);
                }
              }}
            />
          </LocalizationProvider>
        </>
      )}
    </Stack>
  );
};